import { useState, useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { FONT, COLOR, CHIP_TONES } from "../lib/tokens";
import { signOut } from "../lib/useAuth";
import { useWebPush } from "../lib/useWebPush";
import { useNotificationPreferences } from "../lib/useNotificationPreferences";
import { supabase } from "../lib/supabase";
import { getMeasurementSystem, setMeasurementSystem } from "../lib/unitPrefs";
import { DIETARY_OPTIONS } from "../data";
import CityPicker from "./CityPicker";
import { triggerEnrichmentForNewCities } from "../lib/enrichRegionBrands";
import RegionCompleteStatus from "./RegionCompleteStatus";
import { useShoppingRegion } from "../lib/useShoppingRegion";
import AddKidSection from "../experiments/kids-mode/AddKidSection";
import AllergiesSection from "./AllergiesSection";
import NewPasswordSheet from "./NewPasswordSheet";
import AllergySheetReview from "./AllergySheetReview";

// Panel header used at the top of each section in Settings.
function SectionHeader({ label }) {
  return (
    <div style={{ fontFamily:FONT.mono, fontSize:10, color:COLOR.gold, letterSpacing:"0.12em", marginBottom:10, marginTop:24 }}>
      {label}
    </div>
  );
}

function Chip({ color = COLOR.border, bg = COLOR.soil, text = COLOR.dim, children }) {
  return (
    <span style={{ display:"inline-flex", alignItems:"center", background: bg, border:`1px solid ${color}`, color: text, borderRadius:20, padding:"3px 9px", fontFamily:FONT.mono, fontSize:9, letterSpacing:"0.08em" }}>
      {children}
    </span>
  );
}

// Short tag for a connection row so it's clear whether you're looking at a
// family member vs a friend, and — for friends — what their dietary pref is.
function DietaryChip({ profile }) {
  if (!profile?.dietary) return null;
  const LABEL = {
    everything: "ALL", vegetarian: "VEG", vegan: "VEGAN", keto: "KETO",
    carnivore: "CARN", glutenfree: "GF", halal: "HALAL", kosher: "KOSHER",
    dairyfree: "DF",
  };
  const label = LABEL[profile.dietary] || profile.dietary.toUpperCase();
  return <Chip>{label}</Chip>;
}

// A single relationship row (family, friend, or pending). Tapping the
// avatar + name opens the target's profile overlay when that connection
// is accepted. Pending rows stay inert — there's nothing useful to show
// until the other side accepts.
function ConnectionRow({ row, actions, onOpenProfile }) {
  const name = row.other?.name || "Unknown";
  const initial = name[0]?.toUpperCase() || "?";
  const canOpen = !!(onOpenProfile && row.status === "accepted" && row.otherId);

  return (
    <div style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 14px", background:COLOR.ground, border:`1px solid ${COLOR.edge}`, borderRadius:12, marginBottom:8 }}>
      <button
        onClick={canOpen ? () => onOpenProfile(row.otherId) : undefined}
        disabled={!canOpen}
        style={{
          display:"flex", alignItems:"center", gap:12, flex:1, minWidth:0,
          background:"transparent", border:"none", padding:0,
          textAlign:"left", color:"inherit",
          cursor: canOpen ? "pointer" : "default",
        }}
      >
        {row.other?.avatar_url ? (
          <img
            src={row.other.avatar_url}
            alt={name}
            referrerPolicy="no-referrer"
            style={{ width:40, height:40, borderRadius:20, objectFit:"cover", flexShrink:0, display:"block" }}
          />
        ) : (
          <div style={{ width:40, height:40, borderRadius:20, background:COLOR.goldDeep, color:COLOR.gold, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:FONT.serif, fontSize:18, fontWeight:500, flexShrink:0 }}>
            {initial}
          </div>
        )}
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontFamily:FONT.sans, fontSize:14, color:COLOR.ink, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
            {name}
          </div>
          <div style={{ display:"flex", gap:5, marginTop:4, flexWrap:"wrap" }}>
            {row.kind === "family" && <Chip bg="#1a2015" color={COLOR.leafDim} text={COLOR.leafMuted}>FAMILY</Chip>}
            {row.kind === "friend" && <Chip bg={COLOR.skyDeep} color="#1e2a3a" text="#77a3d9">FRIEND</Chip>}
            {row.status === "pending" && row.direction === "outgoing" && <Chip>SENT</Chip>}
            {row.status === "pending" && row.direction === "incoming" && <Chip bg="#2a1e15" color="#3a2a1e" text="#d9a377">INCOMING</Chip>}
            {row.status === "accepted" && <DietaryChip profile={row.other} />}
          </div>
        </div>
      </button>
      <div style={{ display:"flex", gap:6, flexShrink:0 }}>
        {actions}
      </div>
    </div>
  );
}

function SmallButton({ onClick, color = COLOR.gold, children }) {
  return (
    <button
      onClick={onClick}
      style={{ background: color, border:"none", color:COLOR.night, borderRadius:8, padding:"6px 10px", fontFamily:FONT.mono, fontSize:10, fontWeight:600, letterSpacing:"0.08em", cursor:"pointer" }}
    >
      {children}
    </button>
  );
}

function GhostButton({ onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{ background:"transparent", border:`1px solid ${COLOR.border}`, color:COLOR.dim, borderRadius:8, padding:"6px 10px", fontFamily:FONT.mono, fontSize:10, letterSpacing:"0.08em", cursor:"pointer" }}
    >
      {children}
    </button>
  );
}

// Rarity → tier color mapping. Matches the daily-roll palette so the
// loot-box / level-up work coming later reuses the same language.
const RARITY_COLOR = {
  common:   "#8e8e8e",
  uncommon: COLOR.leaf,
  rare:     "#77a3d9",
  ultra:    "#c77dd9",
};

// AVATAR section — collection grid. Owned tiles are clickable; tapping
// one makes it the user's current avatar. Unowned tiles render as 🔒
// silhouettes so the unlock loop reads immediately (future loot-box /
// level-up work grants more catalog rows). Rarity dot in the bottom-
// right uses the daily-roll tier palette.
function AvatarSection({ catalog, owned, currentSlug, onPin }) {
  if (!catalog?.length) return null;
  const ownedCount = owned.size;
  return (
    <>
      <SectionHeader label="YOUR AVATAR" />
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4, 1fr)", gap:10 }}>
        {catalog.map(c => {
          const isOwned   = owned.has(c.slug);
          const isCurrent = currentSlug === c.slug;
          const tier      = RARITY_COLOR[c.rarity] || COLOR.border;
          const clickable = isOwned && typeof onPin === "function";
          return (
            <button
              key={c.slug}
              onClick={clickable ? () => onPin(c.slug) : undefined}
              disabled={!clickable}
              style={{
                position:"relative",
                aspectRatio:"1 / 1",
                padding:0,
                background: isCurrent ? COLOR.goldDeep : COLOR.ground,
                border: `1px solid ${isCurrent ? COLOR.gold : (isOwned ? COLOR.border : COLOR.soil)}`,
                borderRadius:14,
                cursor: clickable ? "pointer" : "default",
                overflow:"hidden",
              }}
              title={isOwned ? c.name : "Locked"}
            >
              {isOwned ? (
                <img
                  src={c.image_url}
                  alt={c.name}
                  style={{ width:"100%", height:"100%", objectFit:"cover", display:"block" }}
                />
              ) : (
                <div style={{ width:"100%", height:"100%", background:COLOR.deep, display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, color:COLOR.border }}>
                  🔒
                </div>
              )}
              <span style={{ position:"absolute", bottom:4, right:4, width:8, height:8, borderRadius:4, background: tier, border:"1px solid rgba(0,0,0,0.4)" }} />
              {isCurrent && (
                <span style={{ position:"absolute", top:4, left:4, fontFamily:FONT.mono, fontSize:8, color:COLOR.night, background:COLOR.gold, padding:"2px 5px", borderRadius:4, letterSpacing:"0.08em", fontWeight:700 }}>
                  CURRENT
                </span>
              )}
            </button>
          );
        })}
      </div>
      <div style={{ fontFamily:FONT.sans, fontSize:11, color:COLOR.muted, marginTop:8, lineHeight:1.5 }}>
        {ownedCount < catalog.length
          ? `Tap any unlocked tile to switch. ${catalog.length - ownedCount} more to unlock.`
          : "Tap any tile to switch. You've unlocked everything available."}
      </div>
    </>
  );
}

// Preferences section — diet + shopping cities. Both axes flow into
// downstream behavior (recipe substitutions; localized brand picker)
// and both can change over time, so they live in Settings, not just
// onboarding. Onboarding seeds the initial values; Settings is the
// always-available editor.
function PreferencesSection({ profile, upsertProfile }) {
  const [savingDiet, setSavingDiet] = useState(false);
  const [savingCities, setSavingCities] = useState(false);
  const [savingToggle, setSavingToggle] = useState(false);
  // Allergy-sheet photo import (parse-allergy-sheet + mandatory
  // review) — saves the confirmed union straight to the profile.
  const [allergySheetOpen, setAllergySheetOpen] = useState(false);
  // Sync layer between profile.shopping_cities (Supabase, what the
  // edge fn reads) and useShoppingRegion's localStorage (what the
  // brand picker filters by). Without this, you can save Springfield
  // to your profile, the edge fn enriches `us-mo-springfield` brands,
  // but the picker still asks for `us` (the default) and filters
  // every regional brand OUT. The setter call in updateCities below
  // pushes the first city through. Plus the backfill effect below
  // handles users who already had shopping_cities set BEFORE this
  // sync shipped.
  const [, setShoppingRegion] = useShoppingRegion();

  const dietary = profile?.dietary || "";
  const shoppingCities = Array.isArray(profile?.shopping_cities)
    ? profile.shopping_cities
    : [];
  // Default ON when the column is null (legacy rows) — matches the
  // migration default. Explicit false from the user disables the
  // auto-trigger.
  const regionalEnabled = profile?.regional_brands_enabled !== false;

  // (The one-time shopping_cities → region backfill moved to
  // syncRegionFromProfile in useShoppingRegion.js, called from App
  // at profile load — running it only when Settings opened left
  // brand pickers on the stale default region for users who set
  // cities during onboarding. The explicit push on city EDIT in
  // updateCities below stays.)

  const pickDiet = async (id) => {
    if (savingDiet) return;
    setSavingDiet(true);
    try {
      await upsertProfile?.({ dietary: id });
    } finally {
      setSavingDiet(false);
    }
  };

  const updateCities = async (next) => {
    if (savingCities) return;
    setSavingCities(true);
    try {
      await upsertProfile?.({ shopping_cities: next });
      // Fire-and-forget enrichment for any newly-added cities. The
      // edge function is idempotent (regions already enriched skip
      // immediately), so re-firing on a remove + re-add is harmless.
      // Only fires when the toggle is on — respects the user's
      // explicit off-switch.
      if (regionalEnabled) {
        triggerEnrichmentForNewCities(shoppingCities, next);
      }
      // Push the primary city into useShoppingRegion's storage so
      // the brand picker's region filter matches what the edge fn
      // wrote. Falls back to "us" when the user clears every city.
      const primary = Array.isArray(next) && next.length > 0 ? next[0] : "us";
      setShoppingRegion(primary);
    } finally {
      setSavingCities(false);
    }
  };

  const toggleRegional = async (next) => {
    if (savingToggle) return;
    setSavingToggle(true);
    try {
      await upsertProfile?.({ regional_brands_enabled: next });
      // If the user JUST enabled it AND has cities set, fire
      // enrichment for those cities now (rather than waiting for
      // the next add). Most-likely path: user just turned it on
      // because they want the suggestions immediately.
      if (next && shoppingCities.length > 0) {
        triggerEnrichmentForNewCities([], shoppingCities);
      }
    } finally {
      setSavingToggle(false);
    }
  };

  return (
    <>
      <SectionHeader label="DIET" />
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        gap: 6,
      }}>
        {DIETARY_OPTIONS.map((d) => {
          const active = dietary === d.id;
          return (
            <button
              key={d.id}
              onClick={() => pickDiet(d.id)}
              disabled={savingDiet}
              className="mcm-focusable"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 12px",
                fontFamily: FONT.sans,
                fontSize: 13,
                fontWeight: active ? 600 : 500,
                color: active ? COLOR.night : COLOR.ink,
                background: active ? COLOR.gold : COLOR.soil,
                border: `1px solid ${active ? COLOR.gold : COLOR.border}`,
                borderRadius: 10,
                cursor: savingDiet ? "wait" : "pointer",
                textAlign: "left",
                touchAction: "manipulation",
                WebkitTapHighlightColor: "transparent",
              }}
            >
              <span style={{ fontSize: 18, lineHeight: 1 }}>{d.emoji}</span>
              <span style={{ flex: 1, minWidth: 0 }}>{d.label}</span>
            </button>
          );
        })}
      </div>
      <div style={{ fontFamily: FONT.sans, fontSize: 12, color: COLOR.dim, marginTop: 8, lineHeight: 1.5 }}>
        Drives recipe suggestions, dietary warnings on items, and
        substitution defaults. Changeable any time.
      </div>

      <AllergiesSection
        profile={profile}
        upsertProfile={upsertProfile}
        onScanPhoto={() => setAllergySheetOpen(true)}
      />
      <AllergySheetReview
        open={allergySheetOpen}
        onClose={() => setAllergySheetOpen(false)}
        existing={Array.isArray(profile?.allergies) ? profile.allergies : []}
        onSave={(merged) => upsertProfile?.({ allergies: merged })}
      />

      <CookingStyleSection profile={profile} upsertProfile={upsertProfile} />

      <SectionHeader label="WHERE YOU SHOP" />
      {/* Enrichment status no longer renders here as a banner. It
          surfaces inline INSIDE CityPicker, replacing the "Use my
          location" button while a region is in_progress / queued.
          Avoids the "pinned-to-top banner" feel — the status sits
          where the action lived, in context with the city it's
          enriching. */}
      {/* Per-city completion status. Renders for cities whose
          regional enrichment has finished — in-progress / queued
          states surface inline inside CityPicker (replacing the
          "Use my location" slot) so two status surfaces don't
          fight for the user's attention. The re-fire button lived
          here during beta; removed for prod since users shouldn't
          be able to re-spend Haiku on already-enriched regions. */}
      {shoppingCities.map((cityKey) => (
        <RegionCompleteStatus key={cityKey} regionKey={cityKey} />
      ))}
      <div style={{
        background: COLOR.soil,
        border: `1px solid ${COLOR.border}`,
        borderRadius: 14,
        padding: 14,
      }}>
        <CityPicker
          value={shoppingCities}
          onChange={updateCities}
          max={3}
          showGeolocate={true}
          compact={true}
        />
      </div>
      <div style={{ fontFamily: FONT.forma, fontSize: 11, color: COLOR.dim, marginTop: 8, lineHeight: 1.5 }}>
        Local brands and store house-brands surface first when you
        scan or search. Pick up to 3 nearby cities — useful if you
        live between metros or shop across them.
      </div>

      {/* Regional brand auto-enrichment toggle. When on, adding a
          new city fires a background Haiku pass that fills in
          local store house-brands (Best Choice for AWG-Midwest,
          Hy-Vee brand, HEB, Wegmans, etc.) so the brand picker is
          localized. Off = the city still saves but no Haiku call
          happens; user keeps the city for filter-only use. */}
      <div style={{
        marginTop: 12,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        background: COLOR.soil,
        border: `1px solid ${COLOR.border}`,
        borderRadius: 14,
        padding: "14px 16px",
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontFamily: FONT.forma,
            fontSize: 13,
            fontWeight: 600,
            color: COLOR.ink,
          }}>
            Auto-fill local brands
          </div>
          <div style={{
            fontFamily: FONT.forma,
            fontSize: 11,
            color: COLOR.dim,
            marginTop: 3,
            lineHeight: 1.4,
          }}>
            When you add a new city, we ask AI for store house-brands
            and regional independents from grocers in that area. Free
            after the first user from that metro — slow build of a
            shared catalog.
          </div>
        </div>
        <button
          onClick={() => toggleRegional(!regionalEnabled)}
          disabled={savingToggle}
          aria-pressed={regionalEnabled}
          className="mcm-focusable"
          style={{
            flexShrink: 0,
            width: 44,
            height: 26,
            borderRadius: 999,
            border: `1px solid ${regionalEnabled ? COLOR.gold : COLOR.border}`,
            background: regionalEnabled ? COLOR.gold : "transparent",
            position: "relative",
            cursor: savingToggle ? "wait" : "pointer",
            padding: 0,
            transition: "background 200ms ease, border-color 200ms ease",
            touchAction: "manipulation",
            WebkitTapHighlightColor: "transparent",
          }}
        >
          <span style={{
            position: "absolute",
            top: 2,
            left: regionalEnabled ? 20 : 2,
            width: 20,
            height: 20,
            borderRadius: "50%",
            background: regionalEnabled ? COLOR.night : COLOR.muted,
            transition: "left 200ms ease",
            boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
          }} />
        </button>
      </div>
    </>
  );
}

/**
 * Settings screen. Shown as a full overlay — tap the × to close.
 *
 * Props:
 *   profile         — current user's profile row (needs .invite_code)
 *   relationships   — object returned by useRelationships()
 *   onClose         — close the overlay
 *   onEditProfile   — (optional) open the profile editor
 */
// Community & privacy — the recipe-publish default + a one-tap
// make-everything-private escape. New recipes publish to the community
// by default (DB trigger reads this preference); personal notes are
// always stripped from community reads. Per-recipe privacy stays on
// each recipe page.
function CommunityPrivacySection({ userId, profile, upsertProfile }) {
  const [busy, setBusy] = useState(false);
  const [allBusy, setAllBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const enabled = profile?.publish_recipes_by_default !== false; // default ON

  const toggle = async (next) => {
    if (busy) return;
    setBusy(true);
    try { await upsertProfile?.({ publish_recipes_by_default: next }); }
    catch (e) { console.warn("[settings] publish-default toggle failed:", e?.message || e); }
    finally { setBusy(false); }
  };

  const makeAllPrivate = async () => {
    if (allBusy || !userId) return;
    setAllBusy(true);
    try {
      const { error } = await supabase
        .from("user_recipes")
        .update({ community_published: false, community_published_at: null })
        .eq("user_id", userId);
      if (error) throw error;
      await upsertProfile?.({ publish_recipes_by_default: false });
      setToast("All your recipes are private now.");
    } catch (e) {
      setToast(e?.message || "Couldn't update — try again.");
    } finally {
      setAllBusy(false);
      setTimeout(() => setToast(null), 3000);
    }
  };

  return (
    <>
      <SectionHeader label="COMMUNITY & PRIVACY" />
      <div style={{ fontFamily: FONT.sans, fontSize: 12, color: COLOR.muted, marginBottom: 12, lineHeight: 1.5 }}>
        New recipes are shared to the community by default so others can
        discover and cook them. Your personal notes always stay private.
        Turn this off to keep new recipes to yourself, or make any single
        recipe private from its own page.
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <span style={{ fontFamily: FONT.forma, fontWeight: 600, fontSize: 14, color: COLOR.ink }}>
          Publish my recipes by default
        </span>
        <button
          type="button"
          onClick={() => toggle(!enabled)}
          disabled={busy}
          className="mcm-focusable"
          style={{
            padding: "8px 16px", borderRadius: 999,
            border: `1px solid ${enabled ? COLOR.gold : COLOR.border}`,
            background: enabled ? COLOR.gold : "transparent",
            color: enabled ? COLOR.night : COLOR.dim,
            fontFamily: FONT.mono, fontSize: 12, fontWeight: 700,
            letterSpacing: "0.08em", cursor: "pointer",
            touchAction: "manipulation", transition: "background 200ms ease",
          }}
        >
          {enabled ? "ON" : "OFF"}
        </button>
      </div>
      <button
        type="button"
        onClick={makeAllPrivate}
        disabled={allBusy}
        className="mcm-focusable"
        style={{
          width: "100%", padding: "12px",
          background: "transparent", border: `1px solid ${COLOR.border}`,
          borderRadius: 12, fontFamily: FONT.mono, fontSize: 12, fontWeight: 600,
          color: COLOR.dim, letterSpacing: "0.06em", cursor: "pointer",
          marginBottom: 10, touchAction: "manipulation",
        }}
      >
        {allBusy ? "MAKING PRIVATE…" : "MAKE ALL MY RECIPES PRIVATE"}
      </button>
      {toast && (
        <div style={{ fontFamily: FONT.sans, fontSize: 12, color: COLOR.leaf, marginBottom: 14 }}>
          {toast}
        </div>
      )}
    </>
  );
}

export default function Settings({
  userId, profile, relationships, upsertProfile,
  avatarCatalog = [], ownedAvatars = new Set(),
  onPinAvatar,
  onClose, onOpenProfile, onOpenReleaseNotes, onOpenAdmin, onOpenSocial,
  onEnterKidMode,
}) {
  // Pantry maintenance — one-shot legacy split. Migrates bulk rows
  // (amount=50 unit=can / reserve_count=49) into per-instance siblings
  // so the StackedItemCard render path can stack them. Idempotent —
  // re-running on already-split rows is a no-op (the WHERE clause in
  // the RPC filters to amount>=2 OR reserve_count>0).
  const [splitBusy, setSplitBusy] = useState(false);
  const [splitResult, setSplitResult] = useState(null); // { splitCount, totalInstances } | { error } | null

  const runSplit = async () => {
    if (!userId || splitBusy) return;
    setSplitBusy(true);
    setSplitResult(null);
    const { data, error } = await supabase.rpc("split_aggregate_rows", { p_user_id: userId });
    setSplitBusy(false);
    if (error) {
      setSplitResult({ error: error.message || String(error) });
      return;
    }
    const row = Array.isArray(data) ? data[0] : data;
    setSplitResult({
      splitCount: Number(row?.split_count) || 0,
      totalInstances: Number(row?.total_instances) || 0,
    });
  };

  // Name editor. Prefilled with whatever the profile has (or empty, which is
  // the whole reason this editor exists — magic-link sign-ins have no name).
  const [nameDraft, setNameDraft] = useState(profile?.name || "");
  // ACCOUNT → set/change password sheet.
  const [passwordSheetOpen, setPasswordSheetOpen] = useState(false);
  const [nameSaved, setNameSaved] = useState(false);
  const [nameBusy, setNameBusy] = useState(false);
  const nameDirty = (nameDraft.trim() || null) !== (profile?.name || null);

  const saveName = async () => {
    const trimmed = nameDraft.trim();
    if (!trimmed) return;
    setNameBusy(true);
    try {
      await upsertProfile({ name: trimmed });
      setNameSaved(true);
      setTimeout(() => setNameSaved(false), 1500);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setNameBusy(false);
    }
  };



  // Relationships data now lives in SocialPage; Settings doesn't
  // touch the family/friend rosters anymore.

  return (
    // Full-viewport backdrop at z:200 so the floating top-corner
    // buttons (🛠 ADMIN / 🔔 BELL / ⚙ SETTINGS, all at z:50) get
    // covered on wider windows — the 480-wide sheet above was
    // centered with maxWidth, leaving the corner space uncovered
    // and the floating buttons peeking through behind the sheet.
    <div style={{ position:"fixed", inset:0, background:COLOR.night, zIndex:200, overflowY:"auto" }}>
      <div style={{ maxWidth:480, margin:"0 auto", padding:"calc(24px + env(safe-area-inset-top)) 20px calc(80px + env(safe-area-inset-bottom))" }}>
        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
          <div>
            <div style={{ fontFamily:FONT.mono, fontSize:10, color:COLOR.gold, letterSpacing:"0.12em" }}>
              SETTINGS
            </div>
            <h1 style={{ fontFamily:FONT.serif, fontSize:32, fontWeight:300, fontStyle:"italic", color:COLOR.ink, margin:"4px 0 0" }}>
              Your circle
            </h1>
          </div>
          <button
            onClick={onClose}
            style={{ background:COLOR.soil, border:`1px solid ${COLOR.border}`, borderRadius:20, width:36, height:36, color:COLOR.dim, fontSize:20, cursor:"pointer", lineHeight:1 }}
          >
            ×
          </button>
        </div>
        <p style={{ fontFamily:FONT.sans, fontSize:13, color:COLOR.dim, lineHeight:1.5, margin:"8px 0 4px" }}>
          Family shares your kitchen, shopping list, and meal plan — both can add or edit. Friends see only your dietary preferences.
        </p>

        {/* Display name — shown to your family & friends on their screens */}
        <SectionHeader label="YOUR NAME" />
        <div style={{ display:"flex", gap:8 }}>
          <input
            value={nameDraft}
            onChange={e => setNameDraft(e.target.value)}
            placeholder="How should you appear to family?"
            maxLength={60}
            style={{ flex:1, padding:"12px 14px", background:COLOR.soil, border:`1px solid ${COLOR.border}`, borderRadius:10, fontFamily:FONT.sans, fontSize:15, color:COLOR.ink, outline:"none" }}
          />
          <SmallButton onClick={saveName}>
            {nameBusy ? "…" : nameSaved ? "SAVED ✓" : nameDirty ? "SAVE" : "SAVE"}
          </SmallButton>
        </div>
        {!profile?.name && (
          <div style={{ fontFamily:FONT.sans, fontSize:11, color:COLOR.dim, marginTop:6 }}>
            Add your name so your family sees who did what.
          </div>
        )}

        {/* Avatar — collection grid. Users arrive with 8 random
            commons and one auto-assigned as their current; tapping
            any other owned tile switches to it. Locked tiles render
            as silhouettes so the unlock loop reads immediately
            (future loot-box / level-up work grants new rows). */}
        <AvatarSection
          catalog={avatarCatalog}
          owned={ownedAvatars}
          currentSlug={profile?.avatar_slug || null}
          onPin={onPinAvatar}
        />

        {/* Preferences — diet + shopping cities. Both flow through
            the same profile.upsert pathway. Diet drives recipe /
            substitution suggestions; shopping cities drives the
            localized brand picker + on-demand brand enrichment. */}
        <PreferencesSection
          profile={profile}
          upsertProfile={upsertProfile}
        />

        <CommunityPrivacySection
          userId={userId}
          profile={profile}
          upsertProfile={upsertProfile}
        />

        {/* Relationships moved to a dedicated SocialPage so this
            screen stops being the dumping ground for every
            social surface. Settings now keeps just the personal
            preferences + device + account stuff. The button
            below routes there — App owns the open state. */}
        <SectionHeader label="FAMILY & FRIENDS" />
        <button
          type="button"
          onClick={onOpenSocial}
          disabled={!onOpenSocial}
          style={{ width: "100%", padding: "14px", background: COLOR.leaf, color: "#fff", border: "none", borderRadius: 12, fontFamily: FONT.mono, fontSize: 12, fontWeight: 600, letterSpacing: "0.08em", cursor: onOpenSocial ? "pointer" : "not-allowed", marginBottom: 14 }}
        >
          MANAGE YOUR PEOPLE →
        </button>
        <div style={{ fontFamily: FONT.sans, fontSize: 11, color: COLOR.muted, marginBottom: 18, lineHeight: 1.5 }}>
          Your handle, share link, phone opt-in, contact-match, and
          family / friend rosters all live there now.
        </div>

        {/* Kids Mode — parent-side roster + "Add a kid" sheet. Gated
            on parent role so the section is invisible on a kid's
            account (which wouldn't see Settings anyway — KidModeShell
            takes over the entire UI). userId may be null briefly
            during sign-in; section handles it. */}
        {userId && <AddKidSection userId={userId} onEnterKidMode={onEnterKidMode} />}

        {/* Push notifications — device-scoped. Each browser + user pair
            subscribes independently; a user signed into the same
            account on laptop + phone ends up with two rows in
            push_subscriptions and gets pushes on both. Opt-in only,
            never prompted at app startup — aggressive prompts train
            users to Block. */}
        <PushNotificationsSection userId={userId} />

        {/* What kinds of notifications to actually receive. Defaults
            are intentionally skewed quiet — "someone added butter to
            the pantry" is off out of the box; meaningful coordination
            (meal scheduled, prep reminders, a friend cooked for you)
            is on. Every toggle here also governs Web Push, because the
            push fanout goes through the notifications table and the
            DB trigger checks should_notify() before inserting. */}
        {userId && <NotificationPreferencesSection userId={userId} />}

        {/* About — release notes entry. Always available so users can
            re-read past notes; also the recovery valve for the silent
            first-paint heuristic in useWhatsNew (new accounts get the
            current version marked-as-seen on first open and need this
            entry to ever see what shipped). */}
        {onOpenReleaseNotes && (
          <>
            <SectionHeader label="ABOUT" />
            <button
              onClick={onOpenReleaseNotes}
              style={{ width:"100%", padding:"14px", background:COLOR.skyDeep, border:`1px solid ${COLOR.skyBorder}`, color:COLOR.sky, borderRadius:12, fontFamily:FONT.mono, fontSize:11, letterSpacing:"0.1em", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}
            >
              <span>📋 RELEASE NOTES</span>
              <span style={{ color:COLOR.sky }}>→</span>
            </button>
          </>
        )}

        {/* Pantry maintenance — opt-in legacy data migrations. Only
            shown to users who have the per-instance pantry refactor
            (always today, post-Phase 1). Splits aggregate bulk rows
            ("amount=50 cans" → 50 sibling rows) so the stacked card
            view applies to data scanned before the refactor. Safe
            to re-run; the RPC's WHERE clause already filters to
            rows that still need splitting. */}
        <SectionHeader label="PANTRY MAINTENANCE" />
        <button
          onClick={runSplit}
          disabled={splitBusy}
          style={{
            width:"100%", padding:"14px",
            background: splitBusy ? COLOR.goldDeep : COLOR.skyDeep,
            border:`1px solid ${COLOR.skyBorder}`, color:COLOR.sky,
            borderRadius:12, fontFamily:FONT.mono,
            fontSize:11, letterSpacing:"0.1em",
            cursor: splitBusy ? "wait" : "pointer",
            display:"flex", alignItems:"center", justifyContent:"space-between",
            marginBottom: splitResult ? 8 : 14,
          }}
        >
          <span>🥫 SPLIT BULK ROWS INTO STACKS</span>
          <span>{splitBusy ? "…" : "→"}</span>
        </button>
        {splitResult && (
          <div style={{
            padding:"10px 12px", marginBottom:14,
            background: splitResult.error ? COLOR.roseDeep : COLOR.leafDeep,
            border: `1px solid ${splitResult.error ? COLOR.roseDim : COLOR.leafDim}`,
            borderRadius:10,
            fontFamily:FONT.sans, fontSize:12,
            color: splitResult.error ? COLOR.rose : COLOR.leafMuted,
            lineHeight: 1.5,
          }}>
            {splitResult.error
              ? `Couldn't split: ${splitResult.error}`
              : splitResult.splitCount === 0
                ? "Nothing to split — your pantry is already per-instance."
                : `Migrated ${splitResult.splitCount} bulk row${splitResult.splitCount === 1 ? "" : "s"} into ${splitResult.totalInstances} stacked instance${splitResult.totalInstances === 1 ? "" : "s"}.`}
          </div>
        )}

        {/* Admin entry — visible only when the viewer's own profile row
            has role='admin' (set via the 0042 migration + a manual
            SQL UPDATE). Intentionally at the bottom near Account so
            it's out of the way for everyday settings edits. */}
        {profile?.role === "admin" && onOpenAdmin && (
          <>
            <SectionHeader label="ADMIN" />
            <button
              onClick={onOpenAdmin}
              style={{ width:"100%", padding:"14px", background:COLOR.roseDeep, border:`1px solid ${COLOR.roseDim}`, color:COLOR.rose, borderRadius:12, fontFamily:FONT.mono, fontSize:11, letterSpacing:"0.1em", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 }}
            >
              <span>🛠 ADMIN TOOLS</span>
              <span>→</span>
            </button>
          </>
        )}

        <MeasurementSystemSection />

        <HandsFreeSection />

        {/* Account footer */}
        <SectionHeader label="ACCOUNT" />
        <button
          onClick={() => setPasswordSheetOpen(true)}
          style={{ width:"100%", padding:"14px", marginBottom:10, background:COLOR.soil, border:`1px solid ${COLOR.border}`, color:COLOR.dim, borderRadius:12, fontFamily:FONT.mono, fontSize:12, letterSpacing:"0.1em", cursor:"pointer" }}
        >
          SET OR CHANGE PASSWORD
        </button>
        <button
          onClick={signOut}
          style={{ width:"100%", padding:"14px", background:COLOR.soil, border:`1px solid ${COLOR.border}`, color:COLOR.dim, borderRadius:12, fontFamily:FONT.mono, fontSize:12, letterSpacing:"0.1em", cursor:"pointer" }}
        >
          SIGN OUT
        </button>
        {passwordSheetOpen && (
          <NewPasswordSheet open onClose={() => setPasswordSheetOpen(false)} />
        )}
      </div>
    </div>
  );
}

// CookingStyleSection — the onboarding persona (mood, not tattoo) +
// the concrete kitchen-tracking toggle. Changing the persona here
// does NOT silently flip the toggle (only onboarding presets it);
// the toggle is what actually gates the pantry tab, pantry-anchored
// AI generation, and match badges.
const COOK_STYLE_OPTIONS = [
  { id: "simple",   label: "Keep it simple" },
  { id: "dayof",    label: "Shop the day I cook" },
  { id: "stocker",  label: "Stocked kitchen" },
  { id: "explorer", label: "Feed me everything" },
];

function CookingStyleSection({ profile, upsertProfile }) {
  const [saving, setSaving] = useState(false);
  const style = profile?.cook_style || null;
  const tracking = profile?.kitchen_tracking !== false;

  const write = async (patch) => {
    if (saving) return;
    setSaving(true);
    try { await upsertProfile?.(patch); }
    finally { setSaving(false); }
  };

  return (
    <>
      <div style={{
        fontFamily: FONT.forma, fontSize: 12, fontWeight: 700, letterSpacing: "0.14em",
        color: COLOR.dim, margin: "22px 0 10px",
      }}>
        COOKING STYLE
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 6 }}>
        {COOK_STYLE_OPTIONS.map((s) => {
          const active = style === s.id;
          return (
            <button
              key={s.id}
              onClick={() => write({ cook_style: active ? null : s.id })}
              disabled={saving}
              className="mcm-focusable"
              style={{
                padding: "11px 12px",
                fontFamily: FONT.forma, fontSize: 13, fontWeight: active ? 700 : 500,
                color: active ? COLOR.night : COLOR.ink,
                background: active ? COLOR.gold : COLOR.soil,
                border: `1px solid ${active ? COLOR.gold : COLOR.border}`,
                borderRadius: 10, cursor: saving ? "wait" : "pointer", textAlign: "left",
                touchAction: "manipulation",
              }}
            >
              {s.label}
            </button>
          );
        })}
      </div>
      <button
        onClick={() => write({ kitchen_tracking: !tracking })}
        disabled={saving}
        className="mcm-focusable"
        style={{
          marginTop: 8, width: "100%", display: "flex", alignItems: "center",
          justifyContent: "space-between", gap: 10, padding: "12px 14px",
          fontFamily: FONT.forma, fontSize: 13, fontWeight: 600,
          color: COLOR.ink, background: COLOR.soil,
          border: `1px solid ${COLOR.border}`, borderRadius: 10,
          cursor: saving ? "wait" : "pointer", touchAction: "manipulation",
        }}
      >
        <span>Track my kitchen inventory</span>
        <span style={{
          fontFamily: FONT.forma, fontSize: 12, fontWeight: 700, letterSpacing: "0.08em",
          color: tracking ? COLOR.leaf : COLOR.dim,
        }}>
          {tracking ? "ON" : "OFF"}
        </span>
      </button>
      <div style={{ fontFamily: FONT.forma, fontSize: 12, color: COLOR.dim, marginTop: 8, lineHeight: 1.5 }}>
        Off = the Kitchen tab hides, recipes generate from your prompt
        instead of your inventory, and missing-ingredient badges stop.
        Great for cooks who shop the same day they cook.
      </div>
    </>
  );
}

// Per-device push enablement. Renders one of four states:
//   * "not-supported" — browser lacks the APIs (older Safari / non-PWA iOS)
//   * "blocked"       — user denied permission at some point
//   * "off"           — supported + allowed, but no active subscription
//   * "on"            — subscription exists in push_subscriptions
function PushNotificationsSection({ userId }) {
  const { supported, permission, enabled, busy, error, enable, disable } = useWebPush(userId);

  if (!supported) {
    return (
      <>
        <SectionHeader label="NOTIFICATIONS" />
        <div style={{
          padding: "12px 14px", marginBottom: 14,
          background: COLOR.ground, border: "1px solid #252525",
          borderRadius: 12,
          fontFamily: FONT.sans, fontSize: 12, color: COLOR.muted, lineHeight: 1.5,
        }}>
          This browser can't receive push notifications. On iOS, add
          mise to your home screen (Share → Add to Home Screen) — the
          Safari PWA supports push.
        </div>
      </>
    );
  }

  const blocked = permission === "denied";

  return (
    <>
      <SectionHeader label="NOTIFICATIONS" />
      <div style={{
        padding: "14px 16px", marginBottom: 14,
        background: enabled ? COLOR.leafDeep : COLOR.ground,
        border: `1px solid ${enabled ? COLOR.leafDim : "#252525"}`,
        borderRadius: 12,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{
              fontFamily: FONT.mono, fontSize: 10, fontWeight: 700,
              color: enabled ? COLOR.leafMuted : COLOR.dim,
              letterSpacing: "0.1em", marginBottom: 4,
            }}>
              ON THIS DEVICE · {enabled ? "ON" : blocked ? "BLOCKED" : "OFF"}
            </div>
            <div style={{ fontFamily: FONT.sans, fontSize: 12, color: COLOR.dim, lineHeight: 1.5 }}>
              {enabled
                ? "Family pantry edits, cook-log reviews, scheduled meals, and badge earns will reach you even when mise is closed."
                : blocked
                  ? "You blocked notifications for this site. Unblock in your browser's site settings (🔒 in the address bar) to enable here."
                  : "Get pinged when your family adds something, schedules a meal, or finishes a cook — even when mise is closed."}
            </div>
          </div>
          <button
            onClick={enabled ? disable : enable}
            disabled={busy || blocked}
            style={{
              padding: "10px 14px", flexShrink: 0,
              background: enabled ? COLOR.soil : COLOR.gold,
              color: enabled ? COLOR.dim : COLOR.night,
              border: `1px solid ${enabled ? COLOR.border : COLOR.gold}`,
              borderRadius: 10,
              fontFamily: FONT.mono, fontSize: 10, fontWeight: 700,
              letterSpacing: "0.08em",
              cursor: (busy || blocked) ? "not-allowed" : "pointer",
              opacity: (busy || blocked) ? 0.5 : 1,
            }}
          >
            {busy ? "…" : enabled ? "TURN OFF" : "ENABLE"}
          </button>
        </div>
        {error && (
          <div style={{
            marginTop: 10, padding: "8px 10px",
            background: COLOR.roseDeep, border: "1px solid #3a1e1e",
            color: COLOR.rose, borderRadius: 8,
            fontFamily: FONT.sans, fontSize: 12,
          }}>
            {error}
          </div>
        )}
      </div>
    </>
  );
}

// ── Notification preferences ────────────────────────────────────────
// Per-user toggles (stored in notification_preferences, migration 0133).
// Every toggle here gates the DB trigger that inserts into the
// notifications table — so a disabled toggle kills BOTH the in-app row
// AND the Web Push delivery (since push is fanned out from the same
// insert). The "ON THIS DEVICE" switch above toggles push-only; the
// switches below toggle notifications at the source.
//
// The key design decision: pantry activity defaults OFF. The app was
// firing a push on every fridge-door event; that trains users to mute
// the app. Meaningful events (meal scheduled, someone will cook for
// you, a prep reminder) stay on by default because those are the ones
// users actually want.
function PrefToggle({ label, hint, value, onChange, tone = COLOR.gold }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12,
      padding: "12px 14px", marginBottom: 8,
      background: value ? "#141612" : COLOR.ground,
      border: `1px solid ${value ? COLOR.leafDim : COLOR.edge}`,
      borderRadius: 12,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: FONT.mono, fontSize: 10, fontWeight: 700,
          color: value ? tone : COLOR.dim,
          letterSpacing: "0.1em", marginBottom: 4,
        }}>
          {label} · {value ? "ON" : "OFF"}
        </div>
        <div style={{ fontFamily: FONT.sans, fontSize: 12, color: COLOR.dim, lineHeight: 1.45 }}>
          {hint}
        </div>
      </div>
      <button
        onClick={() => onChange(!value)}
        style={{
          flexShrink: 0, width: 44, height: 26, padding: 0,
          background: value ? tone : COLOR.border,
          border: `1px solid ${value ? tone : COLOR.border}`,
          borderRadius: 13, cursor: "pointer", position: "relative",
          transition: "background 120ms",
        }}
        aria-pressed={value}
        aria-label={`Toggle ${label}`}
      >
        <span style={{
          position: "absolute", top: 2, left: value ? 20 : 2,
          width: 20, height: 20, borderRadius: 10,
          background: value ? COLOR.night : COLOR.dim,
          transition: "left 120ms",
          display: "block",
        }} />
      </button>
    </div>
  );
}

function NotificationPreferencesSection({ userId }) {
  const { preferences, loading, setPref } = useNotificationPreferences(userId);

  if (loading) return null;

  return (
    <>
      <SectionHeader label="WHAT TO NOTIFY ME ABOUT" />

      <PrefToggle
        label="PREP REMINDERS"
        hint="Start-the-prep reminders before scheduled meals. Cascades all the way back to overnight: freeze the butter at 10pm for tomorrow's pie."
        value={!!preferences.prep_reminders}
        onChange={v => setPref({ prep_reminders: v })}
        tone={CHIP_TONES.category.fg}
      />

      <PrefToggle
        label="COOK TIMER PUSHES"
        hint="Mid-cook pings — 'flip the steak', 'bread out of the oven'. Close the app during a 30-min braise and get a push when it's time to act."
        value={!!preferences.cook_step_timers}
        onChange={v => setPref({ cook_step_timers: v })}
        tone={COLOR.gold}
      />

      <PrefToggle
        label="MEAL COORDINATION"
        hint="Who's cooking, meal rescheduled, requests to cook. Doesn't cover prep — that's above."
        value={!!preferences.meal_coordination}
        onChange={v => setPref({ meal_coordination: v })}
        tone={COLOR.sky}
      />

      <PrefToggle
        label="SOMEONE COOKED FOR YOU"
        hint="When a family member logs a cook and tags you as a diner."
        value={!!preferences.cook_log_diners}
        onChange={v => setPref({ cook_log_diners: v })}
        tone={COLOR.plum}
      />

      <PrefToggle
        label="RECEIPTS"
        hint="One summary per receipt scan. Low volume — the dedup warning fires here too."
        value={!!preferences.receipt_activity}
        onChange={v => setPref({ receipt_activity: v })}
        tone={CHIP_TONES.canonical.fg}
      />

      <PrefToggle
        label="PANTRY SCANS"
        hint="Batch summaries when someone scans the fridge/pantry. One line per scan, not per item."
        value={!!preferences.pantry_scan_activity}
        onChange={v => setPref({ pantry_scan_activity: v })}
        tone={CHIP_TONES.canonical.fg}
      />

      <PrefToggle
        label="SHOPPING LIST"
        hint="Adds, edits, and removals on the shared shopping list."
        value={!!preferences.shopping_activity}
        onChange={v => setPref({ shopping_activity: v })}
        tone={COLOR.sky}
      />

      <PrefToggle
        label="PANTRY FIDDLING"
        hint="Every time someone adds, renames, or deletes a pantry item. Off by default — this is the noisy one."
        value={!!preferences.pantry_activity}
        onChange={v => setPref({ pantry_activity: v })}
        tone={CHIP_TONES.cut.fg}
      />

    </>
  );
}

// "Standard (US)" vs "Metric" toggle. Rewrites the unit-preferences
// map from the chosen system's seed on flip — existing custom picks
// are reset so the new system's defaults actually take effect. The
// per-ingredient UnitPicker still lets the user override any single
// ingredient after choosing a system.
function MeasurementSystemSection() {
  const [system, setSystem] = useState(() => getMeasurementSystem());
  const pick = (next) => {
    if (next === system) return;
    setMeasurementSystem(next);
    setSystem(next);
  };
  const optStyle = (active) => ({
    flex: 1,
    padding: "14px 10px",
    background: active ? COLOR.goldDeep : COLOR.ground,
    border: `1px solid ${active ? COLOR.goldDim : COLOR.border}`,
    color: active ? COLOR.gold : COLOR.dim,
    borderRadius: 10,
    fontFamily: FONT.mono, fontSize: 11, fontWeight: 600,
    letterSpacing: "0.08em", cursor: "pointer", transition: "all 0.15s",
  });
  return (
    <>
      <SectionHeader label="MEASUREMENT SYSTEM" />
      <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
        <button type="button" onClick={() => pick("us")}     style={optStyle(system === "us")}>🇺🇸  STANDARD</button>
        <button type="button" onClick={() => pick("metric")} style={optStyle(system === "metric")}>⚖️  METRIC</button>
      </div>
      <div style={{ fontFamily: FONT.sans, fontSize: 11, color: COLOR.muted, lineHeight: 1.5, marginBottom: 14 }}>
        {system === "us"
          ? "Amounts render in cups, tablespoons, ounces, and pounds. Tap any amount in a recipe to change just that one ingredient."
          : "Amounts render in grams and millilitres. Tap any amount in a recipe to change just that one ingredient."}
      </div>
    </>
  );
}

// Hands-free voice commands toggle. localStorage-backed; the
// CookMode mount of useVoiceCommands reads this on every cook open
// (no live event needed since voice activates per-cook).
const HANDS_FREE_KEY = "mise.handsFree.enabled";

export function isHandsFreeEnabled() {
  if (typeof window === "undefined") return false;
  try { return window.localStorage.getItem(HANDS_FREE_KEY) === "true"; }
  catch { return false; }
}

export function setHandsFreeEnabled(enabled) {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(HANDS_FREE_KEY, enabled ? "true" : "false"); }
  catch { /* ignore */ }
}

function HandsFreeSection() {
  const [enabled, setEnabled] = useState(() => isHandsFreeEnabled());
  // Wake word (OpenWakeWord via WebAudio + ONNX) works in any
  // environment with navigator.mediaDevices and AudioWorklet —
  // Chromium desktop/Android, iOS Safari, the Capacitor iOS app,
  // and Firefox. The post-wake speech-recognition path was removed
  // 2026-05-06; the wake itself IS the command, so SpeechRecognition
  // is no longer a gating requirement.
  const isNative = typeof window !== "undefined" && Capacitor.isNativePlatform();
  const hasAudioPipeline = typeof window !== "undefined"
    && !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
    && typeof AudioWorkletNode !== "undefined";
  const supported = isNative || hasAudioPipeline;

  const toggle = () => {
    const next = !enabled;
    setHandsFreeEnabled(next);
    setEnabled(next);
  };

  const optStyle = (active) => ({
    flex: 1,
    padding: "14px 10px",
    background: active ? COLOR.goldDeep : COLOR.ground,
    border: `1px solid ${active ? COLOR.goldDim : COLOR.border}`,
    color: active ? COLOR.gold : COLOR.dim,
    borderRadius: 10,
    fontFamily: FONT.mono, fontSize: 11, fontWeight: 600,
    letterSpacing: "0.08em", cursor: supported ? "pointer" : "not-allowed",
    opacity: supported ? 1 : 0.5,
    transition: "all 0.15s",
  });

  return (
    <>
      <SectionHeader label="HANDS-FREE COOKING" />
      <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
        <button
          type="button"
          disabled={!supported}
          onClick={() => supported && (enabled ? null : toggle())}
          style={optStyle(enabled)}
        >
          🎙️  ON
        </button>
        <button
          type="button"
          disabled={!supported}
          onClick={() => supported && (!enabled ? null : toggle())}
          style={optStyle(!enabled)}
        >
          OFF
        </button>
      </div>
      <div style={{ fontFamily: FONT.sans, fontSize: 11, color: COLOR.muted, lineHeight: 1.5, marginBottom: 14 }}>
        {!supported
          ? "Voice commands need a browser with mic access and the AudioWorklet API."
          : enabled
            ? "While cooking, say \"Hey Booda\" to advance to the next step. The mic is only on during an active cook. First use will prompt for microphone permission."
            : "Turn on to advance the cook by voice while your hands are full. Wake word: \"Hey Booda.\""}
      </div>
    </>
  );
}
